import React, { ChangeEvent, useEffect, useState } from 'react'
import { getRequest, patchRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { Info } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { Switch, Typography, Grid } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { EtdProviderConfigItem, ClickPostErrorType, iThinkLogisticsType } from 'src/lib/types/integrations'
import {
  getInputType,
  getCharacterLimit,
  getMerchantAttributesPayload,
  defaultClickPostErrorState,
  defaultIthinkLogisticsConfig,
  parseIthinkLogisticsConfig,
  parseSaveIthinkLogisticsConfig,
  parseIthinkLogisticsConfigForBackend,
  handleIthinkLogisticsErrors
} from 'src/lib/utils/integrationsHelpers'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { setMerchantAttributes } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import { useLocale } from 'src/lib/hooks/useLocal'

const IthinkLogistics = () => {
  const { integrations, setIntegrations } = useIntegrations()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const { temporary, setTemporaryData } = useTemporary()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const [iThinkLogisticsConfig, setIthinkLogisticsConfig] = useState<iThinkLogisticsType>(defaultIthinkLogisticsConfig)
  const [errors, setErrors] = useState<ClickPostErrorType>(defaultClickPostErrorState)
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    fetchETDProviderData()
  }, [])

  useEffect(() => {
    if (!temporary) return
    setIthinkLogisticsConfig(temporary.integrations?.ithinkLogistics)
    setTemporaryData(null)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(integrations.iThinkLogistics) !== JSON.stringify(iThinkLogisticsConfig)))
  }, [integrations.iThinkLogistics, iThinkLogisticsConfig])

  useEffect(() => {
    if (!integrations.iThinkLogistics) return
    setIthinkLogisticsConfig(integrations.iThinkLogistics)
    setTemporaryData(null)
    setSettingsUpdated(false)
  }, [integrations.iThinkLogistics, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            IthinkLogistics: iThinkLogisticsConfig
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const handleDiscardSaving = () => {
    if (!integrations.iThinkLogistics) return
    setIthinkLogisticsConfig(integrations.iThinkLogistics)
    setSettingsUpdated(false)
    setTemporaryData(null)
  }

  const fetchETDProviderData = async () => {
    try {
      const results = await Promise.allSettled([
        getRequest('/merchant-shipping-configuration', apiURI.SHIPPING_ENGINE),
        getRequest('/utility/external-etd-config-providers/ITHINK_LOGISTICS/config', apiURI.SHIPPING_ENGINE)
      ])
      results.forEach(result => {
        if (result.status === 'rejected') {
          throw new Error(result.reason)
        }
      })
      setIntegrations({
        ...integrations,
        iThinkLogistics: parseIthinkLogisticsConfig(results)
      })
    } catch (error) {
      errorToast('Error fetching data')
    }
  }

  const saveIThinkLogisticsConfig = async () => {
    const { errors, hasErrors } = handleIthinkLogisticsErrors(iThinkLogisticsConfig)
    setErrors(errors)
    if (hasErrors) return

    setLoaderProperties({ isLoaderVisible: true })

    try {
      const promises: Promise<unknown>[] = [
        patchRequest(
          '/merchant-shipping-configuration',
          apiURI.SHIPPING_ENGINE,
          parseIthinkLogisticsConfigForBackend(iThinkLogisticsConfig)
        )
      ]

      if (!iThinkLogisticsConfig.enabled) {
        const payload = getMerchantAttributesPayload(merchant)
        promises.push(setMerchantAttributes(payload, merchant?.accountId ?? ''))
      }

      const responses: any = await Promise.all(promises)

      if (responses?.[0]?.error) {
        throw new Error(responses[0]?.error)
      }

      if (responses?.[1]) setMerchantData(responses[1]?.data)

      setIntegrations({
        ...integrations,
        iThinkLogistics: parseSaveIthinkLogisticsConfig(responses?.[0], iThinkLogisticsConfig.iThinkLogisticsUIFields)
      })

      successToast('Changes saved')
    } catch (e: any) {
      errorToast(e.message ?? 'Error saving merchant ETD config')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>iThink Logistics is </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${iThinkLogisticsConfig?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='ClickPostToggle'
                checked={iThinkLogisticsConfig?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setIthinkLogisticsConfig({ ...iThinkLogisticsConfig, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>

      <LayoutContainer heading='Credentials'>
        <div className='w-full md:w-full px-2 py-3'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-[10px] border-[1px] border-yellow-faint text-yellow-light rounded-lg w-fit px-[10px] py-2 bg-yellow-faintest'>
              <Info size={16} color='#C7981F' />
              <p className='text-[14px]'>{t('shipping_info')}</p>
            </div>
            {iThinkLogisticsConfig.iThinkLogisticsUIFields.map((item: EtdProviderConfigItem, index: number) => (
              <InputTextField
                key={item.name}
                textFieldId={index.toString()}
                label={item.label}
                isRequired={item.required}
                inputType={getInputType(item.name)}
                autoComplete='new-password'
                inputValue={iThinkLogisticsConfig[item.name as keyof iThinkLogisticsType]}
                handleChange={e => {
                  setIthinkLogisticsConfig({ ...iThinkLogisticsConfig, [item.name]: e.target.value })
                }}
                handleFocus={() => {
                  setErrors({ ...errors, [item.name]: { error: '', errorStatus: false } })
                }}
                customErrorMessage=''
                inputProps={{
                  autoComplete: 'new-password',
                  'data-lpignore': 'true',
                  'data-form-type': 'other',
                  form: {
                    autoComplete: 'off'
                  }
                }}
                fixedCharacterLimit={getCharacterLimit(item.name)}
                errorStatus={errors[item.name as keyof typeof errors]?.errorStatus ?? false}
                errorMessage={errors[item.name as keyof typeof errors]?.error ?? ''}
              />
            ))}
          </div>
        </div>
      </LayoutContainer>

      <SaveNotification
        isVisible={settingsUpdated}
        onSave={saveIThinkLogisticsConfig}
        onDiscard={handleDiscardSaving}
        className='w-full'
      />
    </>
  )
}

export default IthinkLogistics
